import Konva from 'konva';
import Pipeline from '../Pipeline';
import store from '~/scripts/store/index.js';

/**
 * Draw a waveform with bars
 *
 * @param {number[]|Number.<Array[]>} peaks Can also be an array of arrays
 * for split channel rendering
 * @param {number} channelIndex The index of the current channel. Normally
 * should be 0. Must be an integer.
 * @param {number} start The x-offset of the beginning of the area that
 * should be rendered
 * @param {number} end The x-offset of the end of the area that should be
 * rendered
 * @returns {void}
 */

export function drawBars(idx, wavesurferObject, peaks, channelIndex, start, end, classic = false) {
    peaks = normalizePeaks(peaks);
    const wavesurfer = wavesurferObject;
    const drawer = wavesurferObject.drawer;
    const config = store.state.waveConfigurations[idx];
    const layer = store.state.stage.findOne('#waves_layer');
    const group = store.state.stage.findOne(`#wave_image_${idx}`);
    const waveConfiguration = store.state.waveConfigurations[idx];

    if (!config || !group || !layer) {
        return;
    }

    group.destroyChildren();
    group.opacity(Pipeline._getWaveOpacity(config));
    // add transparent rect for better grabability by moving amplitude
    group.add(new Konva.Rect({
        fill: 'red',
        opacity: 0,
        x: 0,
        y: 0,
        width: group.width(),
        height: group.height(),
    }));
    layer.batchDraw();

    return drawer.prepareDraw(
        peaks,
        channelIndex,
        start,
        end,
        ({ absmax, hasMinVals, height, offsetY, halfH, peaks, channelIndex: ch }) => {
            // if drawBars was called within ws.empty we don't pass a start and
            // don't want anything to happen
            if (start === undefined) {
                return;
            }

            drawBar(
                wavesurfer,
                waveConfiguration,
                drawer,
                layer,
                group,
                absmax,
                hasMinVals,
                offsetY,
                halfH,
                peaks,
                ch,
                start,
                end
            );
        }
    );
}

function drawBar(
    wavesurfer,
    waveConfiguration,
    drawer,
    layer,
    group,
    absmax,
    hasMinVals,
    offsetY,
    halfH,
    peaks,
    ch,
    start,
    end,
    classic = false
) {
    // Skip every other value if there are negatives.
    const peakIndexScale = hasMinVals ? 2 : 1;
    const length = peaks.length / peakIndexScale;
    const bar = classic ? 1 * wavesurfer.params.pixelRatio : wavesurfer.params.barWidth * wavesurfer.params.pixelRatio;
    const gap = classic ? 0 :
        (wavesurfer.params.barGap === null
            ? Math.max(wavesurfer.params.pixelRatio, ~~(bar / 2))
            : Math.max(
                wavesurfer.params.pixelRatio,
                wavesurfer.params.barGap * wavesurfer.params.pixelRatio
            ));
    const step = bar + gap;

    const scale = length / drawer.width;
    const first = start;
    const last = end;
    let i = first;
    let count = 0;

    for (i; i < last; i += step) {
        const lowerHeight = classic && (count < 7);
        const peak =
            peaks[Math.floor(i * scale * peakIndexScale)] || 0;
        let h = Math.round((peak / absmax) * halfH);
        const tinyBar = Math.abs(h) < (group.height() * .25);
        h = lowerHeight || tinyBar ? h * .65 : h;

        /* in case of silences, allow the user to specify that we
        * always draw *something* (normally a 1px high bar) */
        if (h == 0 && wavesurfer.params.barMinHeight) {
            h = wavesurfer.params.barMinHeight;
        }

        fillRect(
            wavesurfer,
            drawer,
            layer,
            group,
            waveConfiguration,
            i + drawer.halfPixel,
            halfH - h + offsetY,
            bar + drawer.halfPixel,
            h * 2,
            drawer.barRadius,
            ch,
            1
        );

        count = count == 7 ? 0 : count + 1;
    }
}

/**
 * Draw a waveform with bars
 *
 * @param {number[]|Number.<Array[]>} peaks Can also be an array of arrays
 * for split channel rendering
 * @param {number} channelIndex The index of the current channel. Normally
 * should be 0. Must be an integer.
 * @param {number} start The x-offset of the beginning of the area that
 * should be rendered
 * @param {number} end The x-offset of the end of the area that should be
 * rendered
 * @returns {void}
 */
 export function drawModern(idx, wavesurferObject, peaks, channelIndex, start, end)
 {
    peaks = normalizePeaks(peaks);
    const wavesurfer = wavesurferObject;
    const drawer = wavesurferObject.drawer;
    const config = store.state.waveConfigurations[idx];
    const layer = store.state.stage.findOne('#waves_layer');
    const group = store.state.stage.findOne(`#wave_image_${idx}`);
    const waveConfiguration = store.state.waveConfigurations[idx];

    if (!config || !group || !layer) {
        return;
    }

    group.destroyChildren();
    group.opacity(Pipeline._getWaveOpacity(config));
    // add transparent rect for better grabability by moving amplitude
    group.add(new Konva.Rect({
        fill: 'red',
        opacity: 0,
        x: 0,
        y: 0,
        width: group.width(),
        height: group.height(),
    }));
    layer.batchDraw();

    return drawer.prepareDraw(
        peaks,
        channelIndex,
        start,
        end,
        ({ absmax, hasMinVals, height, offsetY, halfH, peaks }) => {
            // if drawBars was called within ws.empty we don't pass a start and
            // don't want anything to happen
            if (start === undefined) {
                return;
            }
            // Skip every other value if there are negatives.
            const peakIndexScale = hasMinVals ? 2 : 1;
            const length = peaks.length / peakIndexScale;
            const bar = wavesurfer.params.barWidth * wavesurfer.params.pixelRatio;
            const gap =
                wavesurfer.params.barGap === null
                    ? Math.max(wavesurfer.params.pixelRatio, ~~(bar / 2))
                    : Math.max(
                        wavesurfer.params.pixelRatio,
                        wavesurfer.params.barGap * wavesurfer.params.pixelRatio
                    );
            const step = bar + gap;

            const scale = length / drawer.width;
            const first = start;
            const last = end;
            let i = first;

            for (i; i < last; i += step) {
                const peak =
                    peaks[Math.floor(i * scale * peakIndexScale)] || 0;
                let h = Math.round((peak / absmax) * halfH);
                let pixelGap = 2;
                let middleGapAddition = 0;
                let barHeight = h * 2;

                /* in case of silences, allow the user to specify that we
                    * always draw *something* (normally a 1px high bar) */
                if (h == 0 && wavesurfer.params.barMinHeight)
                    h = wavesurfer.params.barMinHeight;

                if (h > 20 || h < -20) {
                    // divide current bar into parts
                    let partY = [0, 0.03, 0.1, 0.30, 0.5, 0.70, 0.9, 0.97, 1];

                    for (let j = 0; j < (partY.length - 1); j++) {
                        let baseY = halfH - h + offsetY;
                        let maxY = baseY + barHeight;
                        let y = baseY + Math.round(barHeight * partY[j]);

                        // add pixel gaps
                        if (j != 0) {
                            y = baseY > halfH ? y - pixelGap : y + pixelGap;

                            if (j == 4) {
                                y = baseY > halfH ? y - middleGapAddition : y + middleGapAddition;
                            }
                        }

                        let drawHeight = maxY - Math.round(barHeight * (1 - partY[j + 1])) - y;
                        if (drawHeight != 0 && j != partY.length - 1) {
                            drawHeight = baseY > halfH ? drawHeight + pixelGap : drawHeight - pixelGap;

                            if (j + 1 == 4) {
                                drawHeight = baseY > halfH ? drawHeight + middleGapAddition : drawHeight - middleGapAddition;
                            }
                        }

                        // fillRect(x, y, width, height, radius)
                        fillRect(
                            wavesurfer,
                            drawer,
                            layer,
                            group,
                            waveConfiguration,
                            i + drawer.halfPixel,
                            y,
                            bar + drawer.halfPixel,
                            drawHeight,
                            wavesurfer.barRadius
                        );
                    }
                } else {
                    fillRect(
                        wavesurfer,
                        drawer,
                        layer,
                        group,
                        waveConfiguration,
                        i + drawer.halfPixel,
                        halfH - pixelGap - middleGapAddition -3,
                        bar + drawer.halfPixel,
                        3,
                        wavesurfer.barRadius
                    );

                    fillRect(
                        wavesurfer,
                        drawer,
                        layer,
                        group,
                        waveConfiguration,
                        i + drawer.halfPixel,
                        halfH + pixelGap + middleGapAddition,
                        bar + drawer.halfPixel,
                        3,
                        wavesurfer.barRadius
                    );
                }
            }
        }
    );
}

/**
 * Draw a waveform
 *
 * @param {number[]|Number.<Array[]>} peaks Can also be an array of arrays
 * for split channel rendering
 * @param {number} channelIndex The index of the current channel. Normally
 * should be 0
 * @param {number?} start The x-offset of the beginning of the area that
 * should be rendered (If this isn't set only a flat line is rendered)
 * @param {number?} end The x-offset of the end of the area that should be
 * rendered
 * @returns {void}
 */
export function drawWave(idx, wavesurferObject, peaks, channelIndex, start, end, classic = false)
{
    peaks = normalizePeaks(peaks);
    const drawer = wavesurferObject.drawer;
    const config = store.state.waveConfigurations[idx];
    const layer = store.state.stage.findOne('#waves_layer');
    const group = store.state.stage.findOne(`#wave_image_${idx}`);
    const waveConfiguration = store.state.waveConfigurations[idx];

    if (!config || !group || !layer) {
        return;
    }

    group.destroyChildren();
    group.opacity(Pipeline._getWaveOpacity(config));
    // add transparent rect for better grabability by moving amplitude
    group.add(new Konva.Rect({
        fill: 'red',
        opacity: 0,
        x: 0,
        y: 0,
        width: group.width(),
        height: group.height(),
    }));

    layer.batchDraw()

    return drawer.prepareDraw(
        peaks,
        channelIndex,
        start,
        end,
        ({ absmax, hasMinVals, height, offsetY, halfH, peaks, channelIndex }) => {
            if (!hasMinVals) {
                const reflectedPeaks = [];
                const len = peaks.length;
                let i = 0;
                for (i; i < len; i++) {
                    reflectedPeaks[2 * i] = peaks[i];
                    reflectedPeaks[2 * i + 1] = -peaks[i];
                }
                peaks = reflectedPeaks;
            }

            // if drawWave was called within ws.empty we don't pass a start and
            // end and simply want a flat line
            if (start !== undefined) {
                if (classic) {
                    drawBar(
                        wavesurferObject,
                        waveConfiguration,
                        drawer,
                        layer,
                        group,
                        absmax,
                        hasMinVals,
                        offsetY,
                        halfH,
                        peaks,
                        channelIndex,
                        start,
                        end,
                        true
                    );
                } else {
                    drawLine(
                        drawer,
                        layer,
                        group,
                        waveConfiguration,
                        peaks,
                        absmax,
                        halfH,
                        offsetY,
                        start,
                        end,
                        channelIndex,
                        classic
                    );
                }
            }
        }
    );
}

/**
 * Tell the canvas entries to render their portion of the waveform
 *
 * @param {number[]} peaks Peaks data
 * @param {number} absmax Maximum peak value (absolute)
 * @param {number} halfH Half the height of the waveform
 * @param {number} offsetY Offset to the top
 * @param {number} start The x-offset of the beginning of the area that
 * should be rendered
 * @param {number} end The x-offset of the end of the area that
 * should be rendered
 * @param {channelIndex} channelIndex The channel index of the line drawn
 */
function drawLine(drawer, layer, group, waveConfiguration, peaks, absmax, halfH, offsetY, start, end, channelIndex, classic = false) {
    const { waveColor, progressColor } = drawer.params.splitChannelsOptions.channelColors[channelIndex] || {};
    drawer.canvases.forEach((entry, i) => {
        drawer.setFillStyles(entry, waveColor, progressColor);
        drawer.applyCanvasTransforms(entry, drawer.params.vertical);
        drawLineToCanvas(drawer, layer, group, waveConfiguration, entry, peaks, absmax, halfH, offsetY, start, end, classic);
    });
}

/**
     * Render the actual waveform line on a `canvas` element
     *
     * @param {CanvasRenderingContext2D} ctx Rendering context of target canvas
     * @param {number[]} peaks Array with peaks data
     * @param {number} absmax Maximum peak value (absolute)
     * @param {number} halfH Half the height of the waveform
     * @param {number} offsetY Offset to the top
     * @param {number} start The x-offset of the beginning of the area that
     * should be rendered
     * @param {number} end The x-offset of the end of the area that
     * should be rendered
     */
function drawLineToCanvas(drawer, layer, group, waveConfiguration, entry, peaks, absmax, halfH, offsetY, start, end, classic = false) {
    const ctx = entry.waveCtx;
    const length = peaks.length / 2;
    const first = Math.round(length * entry.start);

    // use one more peak value to make sure we join peaks at ends -- unless,
    // of course, this is the last canvas
    const last = Math.round(length * entry.end) + 1;

    const canvasStart = first;
    const canvasEnd = last;
    const scale = entry.wave.width / (canvasEnd - canvasStart - 1);

    // optimization
    const halfOffset = halfH + offsetY;
    const absmaxHalf = absmax / halfH;

    ctx.beginPath();
    ctx.moveTo((canvasStart - first) * scale, halfOffset);

    ctx.lineTo(
        (canvasStart - first) * scale,
        halfOffset - Math.round((peaks[2 * canvasStart] || 0) / absmaxHalf)
    );

    let points = [];
    points.push((canvasStart - first) * scale);
    points.push(halfOffset);

    points.push((canvasStart - first) * scale);
    points.push(halfOffset - Math.round((peaks[2 * canvasStart] || 0) / absmaxHalf));

    let i, peak, h;
    for (i = canvasStart; i < canvasEnd; i++) {
        peak = peaks[2 * i] || 0;
        // modified by behind you: get only positive values with Math.abs() to get symmetric waveform
        h = Math.abs(Math.round(peak / absmaxHalf)) * (classic ? .5 : 1);
        // alway draw median line
        h = h == 0 ? 1 : h;
        points.push((i - first) * scale + drawer.halfPixel);
        points.push(halfOffset - h);
        ctx.lineTo((i - first) * scale + drawer.halfPixel, halfOffset - h);
    }

    // draw the bottom edge going backwards, to make a single
    // closed hull to fill
    let j = canvasEnd - 1;
    for (j; j >= canvasStart; j--) {
        // remove original peak algorithm-entry to make waveform symmetric
        // peak = peaks[2 * j + 1] || 0;
        peak = peaks[2 * j] || 0;
        // modified by behind you: get only negative values in with Math.abs() and * -1 to get symmetric waveform
        h = (Math.abs(Math.round(peak / absmaxHalf)) * -1) * (classic ? .5 : 1);
        // alway draw median line
        h = h == 0 ? -1 : h;
        points.push((j - first) * scale + drawer.halfPixel);
        points.push(halfOffset - h);
        ctx.lineTo((j - first) * scale + drawer.halfPixel, halfOffset - h);
    }

    points.push((canvasStart - first) * scale);
    points.push(halfOffset - Math.round((peaks[2 * canvasStart + 1] || 0) / absmaxHalf));

    ctx.lineTo(
        (canvasStart - first) * scale,
        halfOffset -
            Math.round((peaks[2 * canvasStart + 1] || 0) / absmaxHalf)
    );

    ctx.closePath();
    ctx.fill();

    const line = new Konva.Line({
        fill: waveConfiguration.color,
        points: points,
        closed: true,
        listening: false
    });

    if (group) {
        group.add(line);
        layer.batchDraw();
    }
}

/**
 * Draw a rectangle on the multi-canvas
 *
 * @param {number} x X-position of the rectangle
 * @param {number} y Y-position of the rectangle
 * @param {number} width Width of the rectangle
 * @param {number} height Height of the rectangle
 * @param {number} radius Radius of the rectangle
 * @param {channelIndex} channelIndex The channel index of the bar drawn
 */
function fillRect(wavesurfer, drawer, layer, group, waveConfiguration, x, y, width, height, radius, channelIndex, opacity = 1) {
    const startCanvas = Math.floor(x / drawer.maxCanvasWidth);
    const endCanvas = Math.min(
        Math.ceil((x + width) / drawer.maxCanvasWidth) + 1,
        drawer.canvases.length
    );
    let i = startCanvas;
    for (i; i < endCanvas; i++) {
        const entry = drawer.canvases[i];
        const leftOffset = i * drawer.maxCanvasWidth;

        const intersection = {
            x1: Math.max(x, i * drawer.maxCanvasWidth),
            y1: y,
            x2: Math.min(
                x + width,
                i * drawer.maxCanvasWidth + entry.wave.width
            ),
            y2: y + height
        };

        if (intersection.x1 < intersection.x2) {
            const { waveColor, progressColor } = wavesurfer.params.splitChannelsOptions.channelColors[channelIndex] || {};
            drawer.setFillStyles(entry, waveColor, progressColor);
            drawer.applyCanvasTransforms(entry, wavesurfer.params.vertical);

            entry.fillRects(
                intersection.x1 - leftOffset,
                intersection.y1,
                intersection.x2 - intersection.x1,
                intersection.y2 - intersection.y1,
                radius
            );

            const rect = new Konva.Rect({
                fill: waveConfiguration.color,
                stroke: waveConfiguration.color,
                x: intersection.x1 - leftOffset,
                y: intersection.y1,
                width: intersection.x2 - intersection.x1,
                height: intersection.y2 - intersection.y1,
                strokeWidth: 0,
                listening: false,
                opacity: opacity,
            });

            if (group) {
                group.add(rect);
                layer.batchDraw();
            }
        }
    }
}

function normalizePeaks(peaks)
{
    let peaksSum = 0;
    let peaksLength = 0;
    let firstHearablePeak = true;
    let hearablePeaks = [];

    // calculate peaks average height
    for(let peakNr = 0; peakNr < peaks.length; peakNr++)
    {
        const peak = Math.abs(peaks[peakNr]);
        // if (!firstHearablePeak && !isNaN(peak) && peak > 0.05) {
        //     firstHearablePeak = true;
        //     hearablePeaks = peaks.slice(peakNr);
        // }

        hearablePeaks = peaks;
        if (!isNaN(peak) && firstHearablePeak) {
            peaksSum += peak;
            // count peaks length, only if greater 0, because 0 values are not representing the recording volume
            if (peak > 0) {
                peaksLength++;
            }
        }
    }

    const peaksAverage = peaksSum/peaksLength;
    const peaksMultiplikator = 0.3 / peaksAverage;  // calculate multiplicator so that average peak is 0.3
    for(let peakNr = 0; peakNr < hearablePeaks.length; peakNr++)
    {
        const peak = hearablePeaks[peakNr];
        // normalize peak if lower than 0.8
        if (!isNaN(peak) && (Math.abs(peak) * peaksMultiplikator) < 0.8) {
            hearablePeaks[peakNr] = peak * peaksMultiplikator;
        }
    }

    return hearablePeaks;
}
